// infrastructure/api/billingItemApi.js
import BillingItemsDB from 'src/fb/billingItems-db';

import { toBillingItem } from '../mappers/billingItemMapper';

/**
 * Every billing item a clinic has, kept current: a re-projection that clears or changes an item
 * reaches the list without a reload, which is how a biller sees a fix land.
 *
 * THE CLINIC CONSTRAINT IS THE SCOPE, not a convenience: `billingItems` is a flat root collection
 * and the rules require the caller's clinic to match the document's, so a read without it returns
 * nothing at all rather than reaching too far.
 *
 * NO DATE WINDOW, AND THE CLINIC'S OWN HISTORY IS THE BOUND. The period a biller works in is a
 * filter on the table like every other, which means it narrows in memory and changing it must not
 * re-read — so this loads the set those filters run over. "Everything" is smaller than it sounds:
 * the projector never produces an item dated before the clinic's revenue-cycle start date, so a
 * clinic's whole history is exactly what it has billed for, and that is the set a biller searches.
 *
 * THAT IS A BET ON VOLUME, and the one to revisit first. A clinic billing daily for years will
 * eventually make this read too large, and the fix is to page it — the composite index this query
 * already uses, clinic plus service date, is what a pager would need.
 *
 * ORDERED BY SERVICE DATE, ascending, which is the index's order rather than the one a biller
 * reads: every row is in memory and the table sorts them itself, newest first. This order is
 * what a pager would resume from.
 *
 * @param {{clinicId: string}} query
 * @param {(items: import('../../domain/model/BillingItem.types').BillingItem[]) => void} onItems
 * @param {(error: Error) => void} onError
 * @returns {() => void} stops listening
 */
export function watchBillingItems({ clinicId }, onItems, onError) {
  if (!clinicId) {
    onItems([]);

    return () => {};
  }

  return new BillingItemsDB().watchClinic(
    clinicId,
    (documents) => onItems(documents.map(toBillingItem)),
    onError,
  );
} // watchBillingItems()

/**
 * Whether a read failed because the reader may not see billing items, which retrying cannot fix.
 *
 * @param {Error} error
 */
export const isAccessDenied = (error) => error?.code === 'permission-denied';

/**
 * One client's items held for one reason, live: what a task about that client's hold is made of.
 * The composite index on clinic, client and hold reasons is the one the backend counts with.
 *
 * @param {{clinicId: string, clientId: string, holdReason: string}} query
 * @param {(items: import('../../domain/model/BillingItem.types').BillingItem[]) => void} onItems
 * @param {(error: Error) => void} onError
 * @returns {() => void} stops listening
 */
export function watchHeldItems({ clinicId, clientId, holdReason }, onItems, onError) {
  if (!clinicId || !clientId) {
    onItems([]);

    return () => {};
  }

  return new BillingItemsDB().watchHeld(
    { clinicId, clientId, holdReason },
    (documents) => onItems(documents.map(toBillingItem)),
    onError,
  );
} // watchHeldItems()

/**
 * The items held for any of `holdReasons` that name one subject in `field`, live: what a task about
 * that subject is made of, when it covers several causes.
 *
 * @param {{clinicId: string, field: string, value: string, holdReasons: string[]}} query
 * @param {(items: import('../../domain/model/BillingItem.types').BillingItem[]) => void} onItems
 * @param {(error: Error) => void} onError
 * @returns {() => void} stops listening
 */
export function watchHeldItemsAbout({
  clinicId, field, value, holdReasons,
}, onItems, onError) {
  if (!clinicId || !value) {
    onItems([]);

    return () => {};
  }

  return new BillingItemsDB().watchHeldAbout(
    {
      clinicId, field, value, holdReasons,
    },
    (documents) => onItems(documents.map(toBillingItem)),
    onError,
  );
} // watchHeldItemsAbout()
